import { Moon, Plug, Shield, Sun } from 'lucide-react';
import type { ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import {
  assistantMode,
  assistantPatch,
  useObserverOptional,
  type AssistantMode,
} from '@/components/observer/observerContext';
import {
  Badge,
  Button,
  EmptyState,
  Input,
  PageHeader,
  Switch,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@/components/ui';
import { useTheme } from '@/theme/useTheme';
import { SETTINGS } from '../model';
import { AboutKaname } from './AboutKaname';
import { usePrefs } from '../prefs';
import { Body, Page, Panel } from './common';

/*
  Настройки платформы (уровень 2). Раздел «Вид» управляет самой оболочкой: тема, тон второго
  уровня, плотность, окраска выделения по состоянию, помощник, закреплённые приложения. «О Kaname» —
  отдельно внизу: что такое Kaname, рассказ на самом знаке.
*/

/** Режим помощника — тот же выбор, что в ряду «Помощник» на верхней полосе (spec-3 §9). */
const ASSISTANT_MODES: { value: AssistantMode; label: string }[] = [
  { value: 'off', label: 'Выкл' },
  { value: 'home', label: 'На месте' },
  { value: 'guide', label: 'Показывает, где' },
];

export function SettingsScreen() {
  const view = useParams()['*'] || 'profile';
  const label = SETTINGS.groups.flatMap((g) => g.items).find((i) => i.key === view)?.label ?? 'Настройки';
  return (
    <Page>
      <PageHeader title={label} />
      <Body>
        {view === 'view' ? (
          <ViewSettings />
        ) : view === 'profile' ? (
          <Profile />
        ) : view === 'notifications' ? (
          <Notify />
        ) : view === 'about' ? (
          <AboutKaname />
        ) : (
          <Later label={label} />
        )}
      </Body>
    </Page>
  );
}

function Row({ title, hint, children }: { title: string; hint?: string; children: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-6 border-b border-line py-3 last:border-b-0">
      <div className="flex min-w-0 flex-col">
        <span className="text-sm font-medium text-fg">{title}</span>
        {hint && <span className="text-xs text-fg-muted">{hint}</span>}
      </div>
      {children}
    </div>
  );
}

function ViewSettings() {
  const prefs = usePrefs();
  const observer = useObserverOptional();
  const { mode, toggle } = useTheme();
  return (
    <Panel title="Вид оболочки" className="max-w-3xl">
      <Row title="Тема" hint="Раскладка в обеих темах одна.">
        <Button
          variant="secondary"
          size="sm"
          leftIcon={mode === 'dark' ? <Sun /> : <Moon />}
          onClick={toggle}
        >
          {mode === 'dark' ? 'Светлая' : 'Тёмная'}
        </Button>
      </Row>
      <Row title="Меню раздела" hint="Продолжение главного меню или в цвет страницы.">
        <Tabs
          variant="pill"
          value={prefs.sectionTone}
          onValueChange={(v) => prefs.set({ sectionTone: v as 'dark' | 'light' })}
        >
          <TabsList>
            <TabsTrigger value="dark">Тёмное</TabsTrigger>
            <TabsTrigger value="light">Светлое</TabsTrigger>
          </TabsList>
        </Tabs>
      </Row>
      <Row title="Плотно" hint="Списки и таблицы компактнее — для тех, кто сидит в них весь день.">
        <Switch
          size="sm"
          checked={prefs.dense}
          onCheckedChange={(dense) => prefs.set({ dense })}
          aria-label="Плотно"
        />
      </Row>
      <Row
        title="Окраска выделения по состоянию"
        hint="Выделение и ядро в знаке: сбой — красным сразу, операция — синим плавно, покой — коралл."
      >
        <Switch
          size="sm"
          checked={prefs.colorByState}
          onCheckedChange={(colorByState) => prefs.set({ colorByState })}
          aria-label="Окраска по состоянию"
        />
      </Row>
      {/* Помощник — те же настройки, что ряд «Помощник» на верхней полосе: одно хранилище провайдера. */}
      {observer && (
        <Row
          title="Помощник"
          hint="На месте — отклик ядра в знаке; «Показывает, где» — ядро сходит к событию и возвращается. Счётчик, точка и тост — всегда."
        >
          <Tabs
            variant="pill"
            value={assistantMode(observer.settings)}
            onValueChange={(v) => observer.setSettings(assistantPatch(v as AssistantMode))}
          >
            <TabsList aria-label="Помощник">
              {ASSISTANT_MODES.map((m) => (
                <TabsTrigger key={m.value} value={m.value}>
                  {m.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </Row>
      )}
      <Row title="Закрепить «Инфраструктуру» в главном меню" hint="Одно нажатие вместо каталога.">
        <Switch
          size="sm"
          checked={prefs.pinnedInfra}
          onCheckedChange={(pinnedInfra) => prefs.set({ pinnedInfra })}
          aria-label="Закрепить приложение"
        />
      </Row>
    </Panel>
  );
}

function Profile() {
  return (
    <Panel title="Профиль" className="max-w-3xl">
      <div className="grid gap-4 sm:grid-cols-2">
        <Input label="Имя" defaultValue="Демо Пользователь" />
        <Input label="Почта" defaultValue="demo@example.test" disabled />
      </div>
      <div className="mt-4 flex items-center gap-2 text-xs text-fg-muted">
        <Badge variant="neutral">Все сотрудники</Badge>
        <Badge variant="neutral">L2</Badge>
        <Badge variant="coral">Администраторы Kaname</Badge>
      </div>
    </Panel>
  );
}

function Notify() {
  return (
    <Panel title="Куда приходят уведомления" className="max-w-3xl">
      <Row title="В Kaname" hint="Колокольчик в главном меню и шторка справа.">
        <Switch size="sm" defaultChecked aria-label="В Kaname" />
      </Row>
      <Row title="В Slack" hint="Личным сообщением от бота Kaname.">
        <Switch size="sm" aria-label="В Slack" />
      </Row>
    </Panel>
  );
}

function Later({ label }: { label: string }) {
  return (
    <EmptyState
      icon={label === 'Интеграции' ? <Plug /> : <Shield />}
      title={`${label} — позже`}
      description="Раздел появится вместе с ядром: здесь только вид страницы."
    />
  );
}
